import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CheckCircle, XCircle, ArrowLeft, Calendar, MapPin, LogOut, RefreshCw, Plus, Trash2, Package, Pencil, Star } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', description: '', price: '' });
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', country: '', text: '', date: '', rating: 5 });
  const [savingReview, setSavingReview] = useState(false);
  const navigate = useNavigate();

  const adminPassword = sessionStorage.getItem('adminPassword');

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch all reviews
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/all`, {
        headers: {
          'X-Admin-Password': adminPassword
        }
      });

      if (response.ok) {
        const allReviews = await response.json();
        setPendingReviews(allReviews.filter(r => !r.approved));
        setApprovedReviews(allReviews.filter(r => r.approved));
      } else if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        sessionStorage.removeItem('adminPassword');
        navigate('/admin/login');
      }
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setIsLoading(false);
    }
  }, [adminPassword, navigate]);

  const handleApprove = async (reviewName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${encodeURIComponent(reviewName)}/approve`,
        {
          method: 'POST',
          headers: {
            'X-Admin-Password': adminPassword
          }
        }
      );

      if (response.ok) {
        toast.success('Review approved successfully!');
        // Add a small delay to ensure backend has updated
        setTimeout(() => {
          fetchReviews();
        }, 500);
      } else {
        toast.error('Failed to approve review');
      }
    } catch (error) {
      toast.error('Error approving review');
    }
  };

  const handleReject = async (reviewName) => {
    if (!window.confirm(`Are you sure you want to reject the review by ${reviewName}?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${encodeURIComponent(reviewName)}/reject`,
        {
          method: 'DELETE',
          headers: {
            'X-Admin-Password': adminPassword
          }
        }
      );

      if (response.ok) {
        toast.success('Review rejected successfully');
        setTimeout(() => {
          fetchReviews();
        }, 500);
      } else {
        toast.error('Failed to reject review');
      }
    } catch (error) {
      toast.error('Error rejecting review');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPassword');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const openEditReview = (review) => {
    setEditingReview(review);
    setEditForm({
      name: review.name || '',
      country: review.country || '',
      text: review.text || '',
      date: review.timestamp ? new Date(review.timestamp).toISOString().slice(0, 10) : '',
      rating: review.rating || 5,
    });
  };

  const handleInlineDate = async (reviewId, dateValue) => {
    if (!dateValue) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword },
        body: JSON.stringify({ timestamp: new Date(`${dateValue}T12:00:00.000Z`).toISOString() }),
      });
      if (res.ok) {
        toast.success('Date updated');
        fetchReviews();
      } else {
        toast.error('Failed to update date');
      }
    } catch (e) {
      toast.error('Error updating date');
    }
  };

  const handleUpdateReview = async () => {
    if (!editForm.name.trim() || !editForm.text.trim()) {
      toast.error('Name and review text are required');
      return;
    }
    setSavingReview(true);
    try {
      const payload = {
        name: editForm.name,
        country: editForm.country,
        text: editForm.text,
        rating: editForm.rating,
      };
      // Apply the chosen date (stored at 12:00 UTC)
      if (editForm.date) {
        payload.timestamp = new Date(`${editForm.date}T12:00:00.000Z`).toISOString();
      }
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${editingReview.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Password': adminPassword,
          },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        toast.success('Review updated successfully');
        setEditingReview(null);
        fetchReviews();
      } else {
        toast.error('Failed to update review');
      }
    } catch (e) {
      toast.error('Error updating review');
    } finally {
      setSavingReview(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products`, {
        headers: { 'X-Admin-Password': adminPassword },
      });
      if (res.ok) setProducts(await res.json());
    } catch (e) {
      console.error('Failed to fetch products:', e);
    }
  }, [adminPassword]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders`, {
        headers: { 'X-Admin-Password': adminPassword },
      });
      if (res.ok) setOrders(await res.json());
    } catch (e) {
      console.error('Failed to fetch orders:', e);
    }
  }, [adminPassword]);

  useEffect(() => {
    if (!adminPassword) {
      navigate('/admin/login');
      return;
    }
    fetchReviews();
    fetchProducts();
    fetchOrders();
  }, [adminPassword, navigate, fetchReviews, fetchProducts, fetchOrders]);

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      toast.error('Please attach the product PDF file');
      return;
    }
    setSavingProduct(true);
    try {
      const formData = new FormData();
      formData.append('name', productForm.name);
      formData.append('description', productForm.description);
      formData.append('price', productForm.price);
      formData.append('pdf', pdfFile);
      if (coverFile) formData.append('cover', coverFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword },
        body: formData,
      });
      if (res.ok) {
        toast.success('Product added to store!');
        setShowProductForm(false);
        setProductForm({ name: '', description: '', price: '' });
        setPdfFile(null);
        setCoverFile(null);
        fetchProducts();
      } else {
        toast.error('Failed to add product');
      }
    } catch (e) {
      toast.error('Error adding product');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId, name) => {
    if (!window.confirm(`Remove "${name}" from the store?`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': adminPassword },
      });
      if (res.ok) {
        toast.success('Product removed');
        fetchProducts();
      } else {
        toast.error('Failed to remove product');
      }
    } catch (e) {
      toast.error('Error removing product');
    }
  };

  const ReviewCard = ({ review, showActions = true }) => (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{review.name}</CardTitle>
            <div className="flex text-[#04539F] mt-1" data-testid={`review-rating-${review.id}`}>
              {[...Array(5)].map((_, i) => (
                <Star key={`s-${i}`} className={`w-4 h-4 ${i < (review.rating || 5) ? 'fill-current' : 'text-gray-300'}`} />
              ))}
            </div>
            <CardDescription className="flex items-center gap-2 mt-2 flex-wrap">
              <MapPin className="w-4 h-4" />
              {review.country || '—'}
              <span className="mx-1">•</span>
              <Calendar className="w-4 h-4" />
              <input
                type="date"
                defaultValue={review.timestamp ? new Date(review.timestamp).toISOString().slice(0, 10) : ''}
                onChange={(e) => handleInlineDate(review.id, e.target.value)}
                className="border rounded px-2 py-1 text-sm text-gray-700"
                data-testid={`inline-date-${review.id}`}
                title="Change the review date"
              />
            </CardDescription>
          </div>
          <div>
            {review.approved ? (
              <Badge className="bg-green-500">Approved</Badge>
            ) : (
              <Badge className="bg-yellow-500">Pending</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 mb-4 whitespace-pre-line">{review.text}</p>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => openEditReview(review)}
            variant="outline"
            className="flex items-center gap-2 border-[#04539F] text-[#04539F] hover:bg-[#04539F] hover:text-white"
            data-testid={`edit-review-btn-${review.id}`}
          >
            <Pencil className="w-4 h-4" />
            Edit
          </Button>
          {showActions && !review.approved && (
            <>
              <Button
                onClick={() => handleApprove(review.name)}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                data-testid={`approve-review-btn-${review.id}`}
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </Button>
              <Button
                onClick={() => handleReject(review.name)}
                variant="destructive"
                className="flex items-center gap-2"
                data-testid={`reject-review-btn-${review.id}`}
              >
                <XCircle className="w-4 h-4" />
                Reject
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Site
              </Button>
              <h1 className="text-2xl font-bold text-[#04539F]">Admin Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => { fetchReviews(); fetchProducts(); fetchOrders(); }}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pending Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-yellow-600">{pendingReviews.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Approved Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{approvedReviews.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Total Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-[#04539F]">
                {pendingReviews.length + approvedReviews.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-4 border-b">
            <button
              onClick={() => setActiveTab('pending')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-[#04539F] text-[#04539F]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-pending"
            >
              Pending ({pendingReviews.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-b-2 border-[#04539F] text-[#04539F]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-approved"
            >
              Approved ({approvedReviews.length})
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'products'
                  ? 'border-b-2 border-[#04539F] text-[#04539F]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-products"
            >
              Store Products ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'orders'
                  ? 'border-b-2 border-[#04539F] text-[#04539F]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-orders"
            >
              Orders ({orders.length})
            </button>
          </div>
        </div>

        {/* Reviews List */}
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#04539F]" />
            <p className="mt-4 text-gray-600">Loading reviews...</p>
          </div>
        ) : (
          <div>
            {activeTab === 'pending' && (
              <div>
                {pendingReviews.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                      <p className="text-lg font-medium">No pending reviews</p>
                      <p className="text-gray-600 mt-2">All reviews have been processed!</p>
                    </CardContent>
                  </Card>
                ) : (
                  pendingReviews.map((review) => (
                    <ReviewCard key={review.name + review.timestamp} review={review} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'approved' && (
              <div>
                {approvedReviews.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-lg font-medium">No approved reviews yet</p>
                      <p className="text-gray-600 mt-2">Start approving pending reviews!</p>
                    </CardContent>
                  </Card>
                ) : (
                  approvedReviews.map((review) => (
                    <ReviewCard key={review.name + review.timestamp} review={review} showActions={false} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'products' && (
              <div data-testid="products-panel">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Digital Products</h2>
                  <Button
                    onClick={() => setShowProductForm(!showProductForm)}
                    className="bg-[#04539F] hover:bg-[#0668C7] flex items-center gap-2"
                    data-testid="toggle-product-form-btn"
                  >
                    <Plus className="w-4 h-4" />
                    {showProductForm ? 'Cancel' : 'Add Product'}
                  </Button>
                </div>

                {showProductForm && (
                  <Card className="mb-6" data-testid="product-form-card">
                    <CardContent className="pt-6">
                      <form onSubmit={handleCreateProduct} className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">Product Name</label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                            placeholder="e.g. FrancoClaro Ebook"
                            required
                            data-testid="product-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Description</label>
                          <textarea
                            className="w-full border rounded-md p-2 text-sm min-h-[90px]"
                            value={productForm.description}
                            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                            placeholder="Short description of the product"
                            required
                            data-testid="product-description-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Price (€)</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={productForm.price}
                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                            placeholder="15.00"
                            required
                            data-testid="product-price-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">PDF File (delivered to buyer)</label>
                          <Input
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) => setPdfFile(e.target.files[0])}
                            data-testid="product-pdf-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Cover Image (optional)</label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setCoverFile(e.target.files[0])}
                            data-testid="product-cover-input"
                          />
                        </div>
                        <Button
                          type="submit"
                          className="bg-[#04539F] hover:bg-[#0668C7] w-full"
                          disabled={savingProduct}
                          data-testid="save-product-btn"
                        >
                          {savingProduct ? 'Saving...' : 'Save Product'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {products.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No products yet</p>
                      <p className="text-gray-600 mt-2">Add your first digital product to start selling.</p>
                    </CardContent>
                  </Card>
                ) : (
                  products.map((product) => (
                    <Card key={product.id} className="mb-4" data-testid={`admin-product-${product.id}`}>
                      <CardContent className="pt-6 flex items-start justify-between gap-4">
                        <div className="flex gap-4">
                          {product.has_cover && (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/store/products/${product.id}/cover`}
                              alt={product.name}
                              className="w-16 h-16 rounded object-cover border"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold">{product.name}</h3>
                              {!product.active && <Badge className="bg-gray-400">Hidden</Badge>}
                            </div>
                            <p className="text-gray-600 text-sm mt-1">{product.description}</p>
                            <p className="text-[#04539F] font-bold mt-2">€{product.price.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-1">File: {product.file_name}</p>
                          </div>
                        </div>
                        {product.active && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id, product.name)}
                            className="flex items-center gap-1"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" /> Remove
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div data-testid="orders-panel">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Store Orders</h2>
                {orders.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-lg font-medium">No orders yet</p>
                      <p className="text-gray-600 mt-2">Sales will appear here once customers buy.</p>
                    </CardContent>
                  </Card>
                ) : (
                  orders.map((order) => (
                    <Card key={order.id} className="mb-4" data-testid={`order-${order.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{order.product_name}</h3>
                            <p className="text-sm text-gray-600">{order.customer_email}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(order.timestamp).toLocaleString()} · {order.payment_provider}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#04539F]">€{order.amount.toFixed(2)}</p>
                            <Badge className={order.payment_status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'}>
                              {order.payment_status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Review Dialog */}
      <Dialog open={!!editingReview} onOpenChange={(open) => !open && setEditingReview(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-review-dialog">
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-review-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Country</label>
              <Input
                value={editForm.country}
                onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                data-testid="edit-review-country"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Review Text</label>
              <textarea
                className="w-full border rounded-md p-2 text-sm min-h-[140px]"
                value={editForm.text}
                onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                data-testid="edit-review-text"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Rating</label>
              <div className="flex gap-1 mt-1" data-testid="edit-review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setEditForm({ ...editForm, rating: n })}
                    className="p-1"
                    data-testid={`edit-rating-star-${n}`}
                  >
                    <Star className={`w-6 h-6 ${n <= editForm.rating ? 'fill-[#04539F] text-[#04539F]' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Date (when it was originally posted)</label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                data-testid="edit-review-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingReview(null)} data-testid="edit-review-cancel">
              Cancel
            </Button>
            <Button
              className="bg-[#04539F] hover:bg-[#0668C7]"
              onClick={handleUpdateReview}
              disabled={savingReview}
              data-testid="edit-review-save"
            >
              {savingReview ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
